/**
 * Roteamento — TanStack Router, definido em código.
 *
 * Cada tela do sistema atual vira uma rota de verdade, com URL própria. Isso
 * conserta duas coisas que hoje incomodam no dia a dia:
 *  • o `cadcli.html` troca de "view" com JavaScript, então o botão voltar do
 *    navegador sai da tela inteira em vez de voltar um passo;
 *  • os links entre telas são absolutos (`http://192.168.0.173/status.html`),
 *    o que impede trocar o host ou acessar de fora da LAN.
 *
 * Os deep links continuam funcionando: `?os=123` do sistema antigo é
 * redirecionado para `/os/123`.
 */
import { useEffect } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { SECOES, secaoDaRota, secoesEfetivas } from '@tcm/shared';
import type { UsuarioSessao } from '@tcm/shared';
import { useSessao } from '../features/queries';
import { Spinner } from '../components/ui';
import { Login, TrocaObrigatoria } from '../routes/Login';
import { ConsultaPublica } from '../routes/ConsultaPublica';
import { Monitor } from '../routes/Monitor';
import { AppShell } from '../components/layout';
import { Vazio } from '../components/ui';

/**
 * Telas que chegam em ARQUIVO SEPARADO, baixado na primeira vez que alguém
 * abre cada uma.
 *
 * Antes, todas as 19 telas entravam no mesmo `index.js`: quem abria o monitor
 * na TV da oficina baixava, antes de ver a primeira OS, o cadastro de
 * usuários, o editor de automações e o orçamento inteiro. São ~780 KB de
 * JavaScript para desenhar uma tela que usa um sexto disso.
 *
 * O que fica de fora do corte, e por quê:
 *  • `Login`, `Monitor` — são a primeira tela de quem entra. Adiar o código
 *    delas só trocaria a espera de lugar.
 *  • `TrocaObrigatoria` (mesmo arquivo do Login) — a Portaria monta na hora,
 *    sem passar por rota.
 *  • `ConsultaPublica` — o cliente abre um endereço só, direto; para ele um
 *    segundo arquivo seria uma segunda ida à rede antes de ver o status.
 *
 * O `defaultPreload: 'intent'` lá embaixo faz o arquivo da tela começar a
 * baixar quando o mouse passa pelo link, então na prática o clique já
 * encontra o código carregado.
 */
const NovaOs = lazyRouteComponent(() => import('../routes/NovaOs'), 'NovaOs');
const DetalheOs = lazyRouteComponent(() => import('../routes/DetalheOs'), 'DetalheOs');
const ImpressaoOs = lazyRouteComponent(() => import('../routes/ImpressaoOs'), 'ImpressaoOs');
const Orcamento = lazyRouteComponent(() => import('../routes/Orcamento'), 'Orcamento');
const BuscaOrcamento = lazyRouteComponent(() => import('../routes/BuscaOrcamento'), 'BuscaOrcamento');
const StatusOs = lazyRouteComponent(() => import('../routes/StatusOs'), 'StatusOs');
const Clientes = lazyRouteComponent(() => import('../routes/Clientes'), 'Clientes');
const HistoricoCliente = lazyRouteComponent(
  () => import('../routes/HistoricoCliente'),
  'HistoricoCliente',
);
const Caixa = lazyRouteComponent(() => import('../routes/Caixa'), 'Caixa');
const NovoLancamento = lazyRouteComponent(() => import('../routes/NovoLancamento'), 'NovoLancamento');
const Mensagens = lazyRouteComponent(() => import('../routes/Mensagens'), 'Mensagens');
const Dashboard = lazyRouteComponent(() => import('../routes/Dashboard'), 'Dashboard');
const OsZeradas = lazyRouteComponent(() => import('../routes/OsZeradas'), 'OsZeradas');
const Admin = lazyRouteComponent(() => import('../routes/Admin'), 'Admin');
const Usuarios = lazyRouteComponent(() => import('../routes/Usuarios'), 'Usuarios');
const TermosServico = lazyRouteComponent(
  () => import('../routes/TermosServico'),
  'TermosServico',
);
const FluxoAutomacoes = lazyRouteComponent(
  () => import('../routes/FluxoAutomacoes'),
  'FluxoAutomacoes',
);

/**
 * Portaria de todas as rotas. Login é obrigatório: nenhuma tela abre sem
 * sessão confirmada por `/auth/me`.
 *
 * A regra é por AUSÊNCIA de sessão, não por presença de erro 401 — 503, timeout
 * ou resposta estranha também não liberam a passagem. Falhar fechado é o que
 * diferencia "tem login" de "tem tela de login".
 *
 * Ainda assim, a checagem daqui serve para NAVEGAÇÃO: quem protege os dados é
 * a API, que exige o cookie em toda rota. Esconder a tela sem fechar a rota
 * seria teatro.
 */
/**
 * Rotas que abrem SEM sessão. Hoje, só a consulta pública do cliente.
 *
 * A lista é por prefixo e vive aqui, ao lado da Portaria, pelo mesmo motivo
 * que `ROTAS_PUBLICAS` vive ao lado do hook em `backend/src/app.ts`: a
 * exceção precisa ser lida junto com a regra que ela abre.
 *
 * E ela é só metade da história — quem de fato protege os dados é a API.
 * Esta função decide o que a CASCA monta; `/api/v1/publico/os/:token` é a
 * única rota do servidor que responde sem cookie, e é ela que define o que
 * um visitante sem sessão consegue ver.
 */
function ehRotaPublica(caminho: string): boolean {
  return caminho === '/p' || caminho.startsWith('/p/');
}

function Portaria() {
  const navegar = useNavigate();
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  const publica = ehRotaPublica(caminho);
  const naTelaDeLogin = caminho === '/login';
  // A tela do cliente não pergunta quem sou eu. O `/login` continua
  // perguntando: é de lá que sai o "você já está logado, siga em frente".
  const sessao = useSessao(!publica);

  const usuario = sessao.data?.usuario;
  const semSessao = !publica && !sessao.isLoading && !usuario;

  useEffect(() => {
    if (semSessao && !naTelaDeLogin) void navegar({ to: '/login', replace: true });
  }, [semSessao, naTelaDeLogin, navegar]);

  // Antes de tudo: sem spinner de sessão, sem troca de senha obrigatória e
  // sem portaria de seção. Nada disso pertence a quem não é da oficina.
  if (publica) return <Outlet />;

  if (naTelaDeLogin) return <Outlet />;

  if (sessao.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-400">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  // Redirecionamento em andamento: não pisca a tela protegida no meio.
  if (!usuario) return null;

  // Senha provisória bloqueia o sistema inteiro, não só a tela de login —
  // senão bastaria digitar a URL de qualquer tela para pular a troca.
  if (usuario.deveTrocarSenha) return <TrocaObrigatoria nome={usuario.nome} />;

  return <PortariaDeSecao caminho={caminho} usuario={usuario} />;
}

/**
 * Seções que a conta não tem não abrem nem pela URL.
 *
 * Some o item do menu E fecha o endereço: esconder o botão e deixar
 * `/caixa` respondendo a quem digitasse na barra seria decoração.
 *
 * O que isto NÃO é: autorização de dado. Quem fecha a API continua sendo o
 * papel, no backend — uma conta sem a seção "Caixa" não vê a tela, mas o
 * endpoint `/caixa` responde a ela como a qualquer sessão com papel
 * suficiente. É controle de navegação, e é assim que está documentado.
 */
function PortariaDeSecao({ caminho, usuario }: { caminho: string; usuario: UsuarioSessao }) {
  const secao = secaoDaRota(caminho);

  // `secoesEfetivas` e não `usuario.secoes` direto: uma API antiga — ou o
  // fallback do `/auth/me` com o banco fora do ar — pode responder sem o campo,
  // e ler `.includes` de `undefined` derrubaria o sistema inteiro em vez de
  // bloquear uma tela. Sem lista, vale o padrão do papel.
  const secoes = secoesEfetivas(usuario.papel, usuario.secoes);

  // Rota sem seção (login, redirecionamento legado, 404) não é bloqueada aqui.
  if (!secao || secoes.includes(secao)) return <Outlet />;

  const nome = SECOES.find((s) => s.codigo === secao)?.rotulo ?? 'esta área';

  return (
    <AppShell>
      <Vazio
        titulo={`Sem acesso a ${nome}`}
        descricao="Seu usuário não tem esta seção liberada. Quem administra o sistema pode liberá-la em Admin → Usuários → Permissões."
      />
    </AppShell>
  );
}

const rotaRaiz = createRootRoute({
  component: Portaria,
  notFoundComponent: () => (
    <AppShell>
      <Vazio
        titulo="Página não encontrada"
        descricao="O endereço acessado não existe. Use o menu no canto superior direito."
      />
    </AppShell>
  ),
});

const rotaLogin = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/login',
  component: Login,
});

/**
 * A consulta pública do cliente. Caminho curto de propósito: ele é digitado
 * de um papel e colado em WhatsApp, e cada caractere a mais é um caractere
 * a mais para errar.
 */
const rotaConsultaPublica = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/p/$token',
  component: ConsultaPublica,
});

const rotaMonitor = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/',
  component: Monitor,
});

const rotaNovaOs = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/os/nova',
  component: NovaOs,
});

const rotaDetalheOs = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/os/$osNumber',
  component: DetalheOs,
});

/**
 * Via do cliente em A4. Rota própria — e não um modal — porque é um endereço
 * que se abre em outra aba, se manda por link e se reimprime depois.
 */
const rotaImpressaoOs = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/os/$osNumber/pdf',
  component: ImpressaoOs,
});

const rotaOrcamento = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/os/$osNumber/orcamento',
  component: Orcamento,
});

/**
 * Entrada por número da OS, como no `orc.html`. A tela de orçamento em si
 * continua sendo `/os/:n/orcamento` — esta rota só existe para quem chega pelo
 * menu com o número na mão.
 */
const rotaBuscaOrcamento = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/orcamento',
  component: BuscaOrcamento,
});

const rotaStatus = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/os/$osNumber/status',
  component: StatusOs,
});

const rotaClientes = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/clientes',
  component: Clientes,
});

const rotaHistorico = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/clientes/$telefone',
  component: HistoricoCliente,
});

const rotaCaixa = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/caixa',
  component: Caixa,
});

const rotaNovoLancamento = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/caixa/novo',
  component: NovoLancamento,
});

const rotaMensagens = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/mensagens',
  component: Mensagens,
});

const rotaDashboard = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/relatorios/dashboard',
  component: Dashboard,
});

const rotaOsZeradas = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/relatorios/os-zeradas',
  component: OsZeradas,
});

const rotaAdmin = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/admin',
  component: Admin,
});

/**
 * Administração de contas. A tela recusa quem não é admin, mas quem de fato
 * protege é a API: `/api/v1/usuarios` exige papel `admin` em toda rota.
 */
const rotaUsuarios = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/admin/usuarios',
  component: Usuarios,
});

/**
 * O texto dos termos que vai por WhatsApp. Ler é para qualquer sessão — o
 * balcão precisa poder conferir o que o cliente aceitou; escrever é só para
 * `admin`, e quem recusa é a API.
 */
const rotaTermos = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/admin/termos',
  component: TermosServico,
});

const rotaFluxoAutomacoes = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/admin/automacoes',
  component: FluxoAutomacoes,
});

/**
 * Compatibilidade com os links do sistema antigo, que circulam em conversas de
 * WhatsApp e favoritos: `/cadcli.html?os=123` → `/os/123`.
 */
const rotasLegadas = ['/cadcli.html', '/orc.html', '/status.html'].map((caminho) =>
  createRoute({
    getParentRoute: () => rotaRaiz,
    path: caminho,
    beforeLoad: ({ search }) => {
      const os = (search as { os?: string | number }).os;
      // Sem `?os=` não há para onde apontar: o `/orc.html` pelado vira a busca
      // por número, que é o que aquela tela mostrava ao abrir.
      if (!os) throw redirect({ to: caminho === '/orc.html' ? '/orcamento' : '/' });

      const destino =
        caminho === '/orc.html'
          ? '/os/$osNumber/orcamento'
          : caminho === '/status.html'
            ? '/os/$osNumber/status'
            : '/os/$osNumber';

      throw redirect({ to: destino, params: { osNumber: String(os) } });
    },
    component: () => null,
  }),
);

const arvore = rotaRaiz.addChildren([
  rotaLogin,
  rotaConsultaPublica,
  rotaMonitor,
  rotaNovaOs,
  rotaDetalheOs,
  rotaImpressaoOs,
  rotaOrcamento,
  rotaBuscaOrcamento,
  rotaStatus,
  rotaClientes,
  rotaHistorico,
  rotaCaixa,
  rotaNovoLancamento,
  rotaMensagens,
  rotaDashboard,
  rotaOsZeradas,
  rotaAdmin,
  rotaUsuarios,
  rotaTermos,
  rotaFluxoAutomacoes,
  ...rotasLegadas,
]);

export const router = createRouter({
  routeTree: arvore,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
